
// Apple App Configuration
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || 'YOUR_APPLE_CLIENT_ID';
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || window.location.origin;
const APPLE_SCOPE = process.env.REACT_APP_APPLE_SCOPE || 'name email';

// SDK loading state
let sdkLoaded = false;
let sdkLoadingPromise = null;

/**
 * Load Apple SDK asynchronously
 * @returns {Promise<void>}
 */
export const loadAppleSDK = () => {
  // If already loading, return existing promise
  if (sdkLoadingPromise) {
    return sdkLoadingPromise;
  }

  // If already loaded, return resolved promise
  if (sdkLoaded) {
    return Promise.resolve();
  }

  sdkLoadingPromise = new Promise((resolve, reject) => {
    // Check if SDK script already exists
    if (document.getElementById('appleid-signin-script')) {
      sdkLoaded = true;
      resolve();
      return;
    }

    // Check if Client ID is configured
    if (!APPLE_CLIENT_ID || APPLE_CLIENT_ID === 'YOUR_APPLE_CLIENT_ID') {
      console.error('Apple Client ID not configured. Please add REACT_APP_APPLE_CLIENT_ID to your .env file');
      reject(new Error('Apple Client ID not configured'));
      return;
    }

    // Load Apple SDK
    const script = document.createElement('script');
    script.id = 'appleid-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      try {
        // Initialize Apple SDK
        if (window.AppleID) {
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: APPLE_SCOPE,
            redirectURI: APPLE_REDIRECT_URI,
            state: 'origin:web',
            usePopup: true, // Use popup instead of redirect
          });
          console.log('Apple SDK initialized');
          sdkLoaded = true;
          resolve();
        } else {
          console.error(' AppleID object not found after script load');
          sdkLoadingPromise = null;
          reject(new Error('Apple SDK not available'));
        }
      } catch (error) {
        console.error(' Error initializing Apple SDK:', error);
        sdkLoadingPromise = null;
        reject(error);
      }
    };

    script.onerror = () => {
      console.error(`Failed to load Apple SDK - Check network or ad blockers`);
      sdkLoadingPromise = null;
      reject(new Error('Failed to load Apple SDK'));
    };

    // Append script to head
    document.head.appendChild(script);
  });

  return sdkLoadingPromise;
};

/**
 * Open Apple login popup and get user data
 * @returns {Promise<Object>} User data from Apple
 */
export const loginWithApple = async () => {
  try {
    // Ensure SDK is loaded
    await loadAppleSDK();

    if (!window.AppleID) {
      throw new Error('Apple SDK not loaded');
    }

    // Open Apple login popup
    return new Promise((resolve, reject) => {
      try {
        window.AppleID.auth.signIn()
          .then((response) => {
            console.log('Apple Sign-In response:', response);

            // Extract data from response
            const authorization = response.authorization;
            let user = response.user; // Only available on first sign-in!

            // Apple JS SDK may return user as a string
            if (typeof user === 'string') {
              try {
                user = JSON.parse(user);
              } catch (e) {
                console.error('Failed to parse Apple user string:', e);
              }
            }

            if (authorization && authorization.id_token) {
              // Parse the id_token to get user info
              const idToken = authorization.id_token;

              // Decode JWT to get user ID (without verification - backend will verify)
              let payload = {};
              try {
                const tokenParts = idToken.split('.');
                payload = JSON.parse(atob(tokenParts[1]));
              } catch (e) {
                console.error('Failed to decode Apple id_token:', e);
              }

              resolve({
                identityToken: idToken,
                provider_id: payload.sub, // Apple user ID
                authorizationCode: authorization.code,
                // User data (only on first sign-in)
                name: user?.name ? `${user.name.firstName || ''} ${user.name.lastName || ''}`.trim() : null,
                email: user?.email || payload.email || null,
                // Additional data
                isPrivateEmail: payload.is_private_email === 'true',
                emailVerified: payload.email_verified === 'true',
              });
            } else {
              console.error(' No authorization data in Apple response');
              reject(new Error('No authorization data received'));
            }
          })
          .catch((error) => {
            console.error('Apple Sign-In error:', error);

            // Handle user cancellation
            if (error.error === 'popup_closed_by_user') {
              reject(new Error('Login cancelled'));
            } else {
              reject(new Error(error.error || 'Apple sign-in failed'));
            }
          });
      } catch (appleError) {
        console.error('Apple SDK error:', appleError);
        reject(new Error('Apple sign-in failed. Please try again.'));
      }
    });
  } catch (error) {
    console.error('Apple login error:', error);

    // Provide user-friendly error messages
    if (error.message.includes('Apple Client ID not configured')) {
      throw new Error('Apple sign-in is not configured. Please contact support.');
    } else if (error.message.includes('Failed to load Apple SDK')) {
      throw new Error('Unable to load Apple sign-in. Please check your internet connection or disable ad blockers.');
    } else {
      throw error;
    }
  }
};

/**
 * Get Apple login status (not supported by Apple SDK)
 * Apple doesn't provide a way to check login status like Facebook
 * @returns {Promise<Object>}
 */
export const getAppleLoginStatus = async () => {
  console.warn(' Apple does not provide login status check');
  return { status: 'unknown' };
};

export default {
  loadAppleSDK,
  loginWithApple,
  getAppleLoginStatus,
};
